import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useAuth } from '../context/AppContext';
import { Loader2, Mail, Lock, User } from 'lucide-react';
import { toast } from 'sonner';

export default function AuthModal({ isOpen, onClose, mode, setMode }) {
  const { login, register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', name: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'login') {
        await login(form.email, form.password);
        toast.success('Välkommen tillbaka!');
      } else {
        await register(form.email, form.password, form.name);
        toast.success('Konto skapat!');
      }
      onClose();
      setForm({ email: '', password: '', name: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Något gick fel');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-[#F9F9F7]" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="font-['Avenir_Next'] text-2xl text-center">
            {mode === 'login' ? 'Välkommen tillbaka' : 'Skapa konto'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6 pt-4">
          {mode === 'register' && (
            <div className="space-y-2">
              <Label htmlFor="name" className="text-xs uppercase tracking-wider text-[#8C8C8C]">Namn</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8C8C8C]" />
                <Input
                  id="name"
                  type="text"
                  placeholder="Ditt namn"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="pl-10 h-12 rounded-xl border-[#8B6F4E]/20 focus:border-[#8B6F4E]"
                  required
                  data-testid="auth-name-input"
                />
              </div>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-xs uppercase tracking-wider text-[#8C8C8C]">E-post</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8C8C8C]" />
              <Input
                id="email"
                type="email"
                placeholder="din@email.se"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="pl-10 h-12 rounded-xl border-[#8B6F4E]/20 focus:border-[#8B6F4E]"
                required
                data-testid="auth-email-input"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="text-xs uppercase tracking-wider text-[#8C8C8C]">Lösenord</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8C8C8C]" />
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="pl-10 h-12 rounded-xl border-[#8B6F4E]/20 focus:border-[#8B6F4E]"
                required
                minLength={6}
                data-testid="auth-password-input"
              />
            </div>
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full btn-primary h-12"
            data-testid="auth-submit-button"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : mode === 'login' ? (
              'Logga in'
            ) : (
              'Skapa konto'
            )}
          </Button>
        </form>
        <div className="text-center pt-4">
          <button
            onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
            className="text-sm text-[#8C8C8C] hover:text-[#8B6F4E] transition-colors"
            data-testid="auth-toggle-mode"
          >
            {mode === 'login' ? 'Har du inget konto? Registrera dig' : 'Har du redan ett konto? Logga in'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
